import { createContext, type ReactNode } from "react";

export type MarketScope = "futures" | "crypto" | "all";

// SFWL-017 Codex P1 fix: tenant resolution lifecycle, tracked separately from
// the (possibly still-default) context values themselves so consumers can
// fail closed on the CFTC §1.55(b) gate while the real tenant is unknown,
// instead of silently trusting DEFAULT_TENANT_CONTEXT's `marketScope: "all"`.
//   - "default": no subdomain (or a reserved one) — DEFAULT_TENANT_CONTEXT is
//     correct, not a placeholder. Gate-ready.
//   - "pending": a subdomain is present and resolution (cache check + fetch)
//     hasn't completed yet. NOT gate-ready — the real marketScope is unknown.
//   - "resolved": the by-subdomain fetch (or a fresh cache hit) succeeded.
//     Gate-ready.
//   - "failed": the fetch errored, returned non-OK, or timed out. Falls back
//     to DEFAULT_TENANT_CONTEXT for display purposes, but is permanently NOT
//     gate-ready for this subdomain — the real marketScope could be
//     "futures" and we must not assume otherwise.
export type TenantResolution = "default" | "pending" | "resolved" | "failed";

export interface TenantContextValue {
  id: string | null;
  displayName: string | null;
  logoUrl: string | null;
  disclosureFooter: string | null;
  marketScope: MarketScope;
  resolution: TenantResolution;
}

export const DEFAULT_TENANT_CONTEXT: TenantContextValue = {
  id: null,
  displayName: null,
  logoUrl: null,
  disclosureFooter: null,
  marketScope: "all",
  resolution: "default",
};

export const TenantContext = createContext<TenantContextValue>(DEFAULT_TENANT_CONTEXT);

/**
 * SFWL-017 Codex P1 fix: whether it's safe to treat `marketScope` as final.
 * `"pending"` and `"failed"` mean the real tenant scope is unknown — callers
 * gating on `marketScope === "futures"` must fail closed (block, not allow)
 * while this is false, rather than trusting the "all" default.
 */
export function isTenantGateReady(resolution: TenantResolution): boolean {
  return resolution === "default" || resolution === "resolved";
}

export function TenantContextProvider({
  value,
  children,
}: {
  value: TenantContextValue;
  children: ReactNode;
}) {
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
